/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';

import { DetailStatStrip, type DetailStatItem } from '@/shared/ui';
import { formatStatsCurrency } from '@/shared/utils/format-stats';

import type { Sale, SaleCommissionPanel } from '../types/sale.types';

/**
 * The money band across the top of the sale detail screen.
 *
 * Every figure here comes off the backend's commission panel. None of it is
 * recomputed from `accepted_value × commission_percentage`, because the
 * panel already accounts for split rules the front end does not model, and a
 * second arithmetic would quietly disagree with the first.
 *
 * The paperwork progress and the record's state used to sit in this row as
 * two more cards, saying nothing numeric. They now live in the header's
 * status band and the right column respectively, which leaves this as four
 * amounts that answer one question: what did the sale earn, and how much of
 * it has arrived.
 */

export interface SaleKpiCardsProps {
  sale: Sale;
  commission: SaleCommissionPanel | null;
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * The accepted value, as the record stores it.
 *
 * @param sale - The sale record.
 * @param t - The namespace translator.
 * @returns The first strip item.
 */
function valueItem(sale: Sale, t: Translate): DetailStatItem {
  return {
    id: 'value',
    testId: 'sale-kpi-value',
    label: t('detail.kpiCards.saleValue'),
    value: formatStatsCurrency(sale.accepted_value),
  };
}

/**
 * Commission received, against what the panel expects.
 *
 * @param panel - The derived commission panel, null while loading.
 * @param outstanding - What the panel still has to arrive.
 * @param t - The namespace translator.
 * @returns The second strip item.
 */
function commissionItem(
  panel: SaleCommissionPanel | null,
  outstanding: number,
  t: Translate,
): DetailStatItem {
  return {
    id: 'commission',
    testId: 'sale-kpi-commission',
    label: t('detail.kpiCards.commission'),
    value: panel ? formatStatsCurrency(panel.received) : '—',
    secondary: panel
      ? t('detail.kpiCards.commissionOf', { expected: formatStatsCurrency(panel.expected) })
      : undefined,
    accent: panel && outstanding > 0 ? 'var(--accent-mostarda)' : 'var(--accent-salvia)',
  };
}

/**
 * What is still to arrive.
 *
 * @param panel - The derived commission panel, null while loading.
 * @param outstanding - What the panel still has to arrive.
 * @param t - The namespace translator.
 * @returns The third strip item.
 */
function outstandingItem(
  panel: SaleCommissionPanel | null,
  outstanding: number,
  t: Translate,
): DetailStatItem {
  return {
    // The testid predates the redesign, when this slot held the paperwork
    // milestone. Kept as-is so no test loses its selector; renaming is a
    // separate sweep.
    id: 'outstanding',
    testId: 'sale-kpi-paperwork',
    label: t('detail.kpiCards.commissionOutstandingLabel'),
    value: panel ? formatStatsCurrency(outstanding) : '—',
    accent: outstanding > 0 ? 'var(--accent-cobre)' : undefined,
    title: panel && outstanding === 0 ? t('detail.kpiCards.commissionSettled') : undefined,
  };
}

/**
 * The agreed commission percentage.
 *
 * @param sale - The sale record.
 * @param t - The namespace translator.
 * @returns The fourth strip item.
 */
function shareItem(sale: Sale, t: Translate): DetailStatItem {
  return {
    id: 'share',
    testId: 'sale-kpi-share',
    label: t('detail.kpiCards.commissionShare'),
    value: sale.commission_percentage != null ? `${sale.commission_percentage}%` : '—',
    alignEnd: true,
    title:
      sale.commission_percentage == null
        ? t('detail.kpiCards.saleValueHintNoCommission')
        : undefined,
  };
}

/**
 * Builds the four figures.
 *
 * One builder per card: the strip is four independent shapes, and holding
 * them in one function put eight conditionals in a single scope.
 *
 * @param sale - The sale record.
 * @param panel - The derived commission panel, null while loading.
 * @param t - The namespace translator.
 * @returns The strip items.
 */
function buildItems(
  sale: Sale,
  panel: SaleCommissionPanel | null,
  t: Translate,
): DetailStatItem[] {
  const outstanding = panel?.outstanding ?? 0;

  return [
    valueItem(sale, t),
    commissionItem(panel, outstanding, t),
    outstandingItem(panel, outstanding, t),
    shareItem(sale, t),
  ];
}

/**
 * The sale's money strip.
 *
 * @param props.sale - The sale record.
 * @param props.commission - The derived commission panel, null while loading.
 * @returns The rendered strip.
 */
export function SaleKpiCards({ sale, commission }: SaleKpiCardsProps) {
  const { t } = useTranslation('features/sales');

  return (
    <DetailStatStrip testId="sale-kpi-cards" items={buildItems(sale, commission, t)} />
  );
}

export default SaleKpiCards;
